import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  FieldValues,
  useForm,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { supabase } from "../../supabaseClient";
import { ErrorAlert } from "../_ui/ErrorAlert";
import { Disc, Plus, Trash2, X } from "react-feather";
import { SavedContent, SavedContents } from "../../types/api/SavedContent";
import { PostgrestError } from "@supabase/supabase-js";
import { Feed, Feeds } from "../../types/api/Feed";
import { getFeeds } from "../../api/feeds";
import { handleError } from "../../utils/handleError";
import { HashtagList } from "../HashtagList";
import { FlexAlign } from "../_ui/FlexAlign";
import { Card } from "../_ui/Card/Card";

// Helper function to format position values for display
const formatPosition = (position: string): string => {
  switch (position) {
    case "USE_TEMPLATE":
      return "Use Template";
    case "START":
      return "Start";
    case "END":
      return "End";
    case "INLINE":
      return "Inline";
    default:
      return position.replace("_", " ").toLowerCase();
  }
};

// Helper function to generate slug from name
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_-]/g, "") // Remove invalid characters
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/_{2,}/g, "_") // Replace multiple underscores with single
    .replace(/^_+|_+$/g, ""); // Remove leading/trailing underscores
};

export function SavedDescriptions({
  open,
  close,
  updateContent,
  name,
  watch,
  setValue,
  useModal,
}: {
  open?: boolean;
  close?: () => void;
  updateContent?: (desc: SavedContent) => void;
  name?: string;
  watch?: UseFormWatch<FieldValues>;
  setValue?: UseFormSetValue<FieldValues>;
  useModal: boolean;
}) {
  const [savedContent, setSavedContent] = useState<SavedContents | null>([]);
  const [selected, setSelected] = useState<SavedContent | null>(null);
  const [apiError, setApiError] = useState<PostgrestError | null>(null);
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const getFeedsList = useCallback(async () => {
    try {
      if (!feeds || feeds.length === 0) setFeeds(await getFeeds());
    } catch (e) {
      handleError(e as Error);
    }
  }, [feeds]);

  useEffect(() => {
    getSavedContent();
    getFeedsList();
  }, [getFeedsList]);

  const getSavedContent = async () => {
    const { data, error } = await supabase.from("saved_content").select("*");
    setApiError(error);
    setSavedContent(data as SavedContent[]);
    setSelected((data?.[0] as SavedContent) ?? null);
    setIsEditing(false);
  };

  const edit = (description: SavedContent) => {
    setSelected(description);
    setIsEditing(true);
  };

  const addContentToDescription = (desc: SavedContent) => {
    updateContent?.(desc);
    close?.();
  };
  const addContentToAllFeedsDescriptions = (desc: SavedContent) => {
    const mainHashtags = desc.hashtags?.main || [];

    if (name === "default") {
      const content = desc.content || "";
      setValue?.(
        "default.post_content",
        watch?.("default.post_content") +
          `{{VARIANTS(${desc.id}): "${content}"}}`
      );

      // Add hashtags for default
      const currentHashtags = watch?.("default.hashtags") ?? [];
      console.log("currentHashtags", currentHashtags);
      console.log("mainHashtags", mainHashtags);
      setValue?.("default.hashtags", [
        `#VARIANTS(${desc.id}): ${(mainHashtags ?? []).join(" ")}`,
        ...currentHashtags,
      ]);
    } else {
      for (const f of feeds.sort((a, b) => a.slug!.localeCompare(b.slug!))) {
        const contentField = `${f.slug!}.post_content`;
        const hashtagsField = `${f.slug!}.hashtags`;

        const feedContent = desc.variants[f.slug!] ?? desc.content ?? "";
        setValue?.(contentField, watch?.(contentField) + feedContent);

        // Add hashtags for each feed
        if (mainHashtags.length > 0) {
          const currentHashtags = watch?.(hashtagsField) ?? [];
          const newHashtags = [...currentHashtags, ...mainHashtags];
          const uniqueHashtags = Array.from(new Set(newHashtags));
          setValue?.(hashtagsField, uniqueHashtags);
        }
      }
    }

    close?.();
  };

  const content = (
    <>
      <ErrorAlert error={apiError} />

      <div className="flex gap-8">
        {/* Left Column - List of Saved Descriptions */}
        <div className="flex flex-col w-full max-w-80">
          <div className="overflow-y-auto space-y-4">
            <button
              className="btn btn-primary mb-4"
              onClick={() => setSelected(null)}
              disabled={isEditing || !selected}
            >
              Add New Description
            </button>

            {savedContent &&
              savedContent.map((desc) => (
                <div
                  key={desc.id}
                  className={`card bg-base-100 cursor-pointer transition-all hover:shadow-sm ${
                    selected?.id === desc.id ? "bg-base-200" : ""
                  }`}
                  onClick={() => setSelected(desc)}
                >
                  <div className="card-body p-3 cursor-pointer">
                    <div className="flex items-center gap-2 mb-1">
                      <h5 className="font-medium text-sm mb-0">{desc.name}</h5>
                      {desc.auto_add_search && (
                        <div className="badge badge-primary badge-xs">
                          🔍 Auto: {desc.auto_add_search}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-base-content/70 line-clamp-2">
                      {desc.content}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Right Column - Selected Description Details */}

        <div className="flex-1 flex flex-col">
          <FlexAlign className="mb-4">
            <div className="prose">
              <h2>{selected ? "Description Details" : "New Description"}</h2>
            </div>
            <div className="flex items-center gap-2">
              {selected && !isEditing && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => edit(selected)}
                >
                  Edit
                </button>
              )}
              {isEditing && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </FlexAlign>

          <div className="flex-1 overflow-y-auto">
            {selected ? (
              <div className="space-y-4">
                {!isEditing && (
                  <div>
                    <SavedDescriptionDetails
                      description={selected as SavedContent}
                      isEditing={isEditing}
                    />

                    {useModal && (
                      <div className="flex gap-2 pt-1 pb-2">
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() =>
                            addContentToAllFeedsDescriptions(selected)
                          }
                        >
                          Add to all feeds
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => addContentToDescription(selected)}
                        >
                          Add to current feed
                        </button>
                      </div>
                    )}

                    {Object.entries(selected.variants || {}).length > 0 && (
                      <div className="mt-4 mb-2">
                        <div className="prose">
                          <h3 className="font-medium">Variants</h3>
                        </div>
                      </div>
                    )}

                    <ul className="timeline timeline-vertical timeline-compact">
                      {Object.entries(selected.variants || {}).map(
                        ([variant, content], i) => (
                          <li
                            key={variant}
                            style={{
                              gridTemplateColumns: "0px 16px auto",
                            }}
                          >
                            {i > 0 && <hr className="bg-neutral opacity-50" />}
                            <div className="timeline-middle">
                              <Disc size={16} />
                            </div>
                            <div className="timeline-end ml-4 flex-1">
                              <SavedDescriptionDetails
                                description={selected}
                                isEditing={isEditing}
                                variant={variant}
                              />
                            </div>

                            {i <
                              Object.entries(selected.variants || {}).length -
                                1 && <hr className="bg-neutral opacity-50" />}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}

                {/* Show edit form if this description is being edited */}
                {isEditing && (
                  <div className="border-t pt-4">
                    <NewOrEdit
                      id={selected.id}
                      name={selected.name!}
                      content={selected.content}
                      variants={selected.variants!}
                      hashtags={selected.hashtags}
                      autoAddSearch={selected.auto_add_search}
                      position={selected.position}
                      hashtagPosition={selected.hashtag_position}
                      tagPosition={selected.tag_position}
                      slug={selected.slug}
                      setApiError={setApiError}
                      onUpdate={async () => {
                        await getSavedContent();
                        setIsEditing(false);
                      }}
                      feeds={feeds}
                      useModal={useModal}
                    />
                  </div>
                )}
              </div>
            ) : (
              <NewOrEdit
                setApiError={setApiError}
                onUpdate={getSavedContent}
                feeds={feeds}
                useModal={useModal}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );

  const modalContent = (
    <>
      <input
        type="checkbox"
        id={`saved-content-modal-${name}`}
        className="modal-toggle"
        checked={open}
        readOnly
      />
      <div className="modal py-4">
        <div className="modal-box w-11/12 max-w-5xl max-h-[96vh] bg-base-200">
          <span className="prose">
            <h3 className="font-bold mb-2">Saved Descriptions</h3>
          </span>

          <button
            onClick={close}
            className="btn btn-sm btn-circle btn-ghost absolute right-3 top-3"
          >
            ✕
          </button>

          {content}

          <div className="modal-action">
            <button type="button" className="btn" onClick={close}>
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );

  if (!useModal) {
    return (
      <div>
        <div className="container mx-auto max-w-5xl mt-12">
          <div className="prose">
            <h1 className="mb-8">Saved Descriptions</h1>
          </div>
          {content}
        </div>
      </div>
    );
  } else {
    return createPortal(
      modalContent,
      document.getElementById("saved-content-modal")!
    );
  }
}

function NewOrEdit({
  name,
  content,
  id,
  setApiError,
  onUpdate,
  feeds,
  variants,
  hashtags: initialHashtags,
  autoAddSearch,
  position,
  hashtagPosition,
  tagPosition,
  slug: initialSlug,
  useModal,
}: {
  name?: string;
  content?: SavedContent["content"];
  id?: number;
  feeds?: Feeds;
  variants?: { [key: string]: string };
  hashtags?: { main: string[]; variants: { [key: string]: string[] } };
  autoAddSearch?: SavedContent["auto_add_search"];
  position?: SavedContent["position"];
  hashtagPosition?: SavedContent["hashtag_position"];
  tagPosition?: SavedContent["tag_position"];
  slug?: SavedContent["slug"];
  setApiError: (err: PostgrestError | null) => void;
  onUpdate: () => Promise<void>;
  useModal: boolean;
}) {
  const variantRef = useRef(null);
  const [variantsList, setVariantsList] = useState<string[]>(
    variants ? Object.keys(variants) : []
  );

  const [hashtags, setHashtags] = useState<string[]>(
    initialHashtags?.main || []
  );
  const [variantHashtags, setVariantHashtags] = useState<{
    [key: string]: string[];
  }>(initialHashtags?.variants || {});

  const { register, unregister, getValues, setValue, reset, watch } = useForm({
    defaultValues: {
      name: id ? name : null,
      content: id ? content : null,
      auto_add_search: id ? autoAddSearch : null,
      position: id ? position : "USE_TEMPLATE",
      hashtag_position: id ? hashtagPosition : "START",
      tag_position: id ? tagPosition : "START",
      slug: id ? initialSlug : null,
      variants: variants ?? {},
    },
  });

  const watchedName = watch("name");
  const watchedSlug = watch("slug");

  // Auto-generate slug when name changes and slug is empty
  useEffect(() => {
    if (watchedName && !watchedSlug) {
      const generatedSlug = generateSlug(watchedName);
      setValue("slug", generatedSlug);
    }
  }, [watchedName, watchedSlug, setValue]);

  const onSubmit = async () => {
    const values = getValues();

    const { data: session } = await supabase.auth.getSession();

    // Create a combined hashtags object that includes main hashtags and variant-specific hashtags
    const combinedHashtags = {
      main: hashtags,
      variants: variantHashtags,
    };

    const newContent: Omit<SavedContent, "id" | "created_at"> = {
      name: values.name!,
      content: values.content!,
      auto_add_search: values.auto_add_search || null,
      position: values.position || "USE_TEMPLATE",
      hashtag_position: values.hashtag_position || "START",
      tag_position: values.tag_position || "START",
      slug: values.slug || generateSlug(values.name || ""),
      variants: values.variants,
      hashtags: combinedHashtags,
      user_id: session.session?.user.id!,
    };

    const { error } = await supabase.from("saved_content").upsert({
      id,
      ...newContent,
    });
    setApiError(error);

    reset();
    setHashtags([]);
    setVariantHashtags({});
    onUpdate();
  };

  const deleteDesc = async () => {
    await supabase.from("saved_content").delete().eq("id", id!);
    await onUpdate();
  };

  const addVariant = () => {
    const value = (variantRef?.current as unknown as HTMLSelectElement)
      ?.value as string;
    console.log("ADD VARIANT", value);
    if (value) {
      setVariantsList((l) => [...l, value]);
      setVariantHashtags((prev) => ({
        ...prev,
        [value]: hashtags,
      }));
      setValue(`variants.${value}`, getValues().content ?? ""); // Initialize empty content for new variant
    }
  };

  const removeVariant = (variant: string) => {
    unregister(`variants.${variant}`);
    setVariantsList(variantsList.filter((v) => v !== variant));
    setVariantHashtags((prev) => {
      const updated = { ...prev };
      delete updated[variant];
      return updated;
    });
  };

  const filteredFeeds = feeds?.filter((f) => !variantsList.includes(f.slug!));

  return (
    <form>
      <Card>
        <>
          <div className="form-control">
            <label className="label">
              <span className="label-text">Name</span>
            </label>
            <input
              {...register("name", { required: true })}
              className="input  w-full mb-2"
            />
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text">Slug</span>
            </label>
            <input
              {...register("slug", {
                required: true,
                pattern: {
                  value: /^[a-zA-Z0-9_-]+$/,
                  message:
                    "Only alphanumeric characters, dashes, and underscores are allowed",
                },
              })}
              className="input w-full mb-2"
              placeholder="auto-generated-from-name"
            />
            <p className="text-xs text-base-content/60 mb-2">
              Auto-generated from name. Only letters, numbers, dashes, and
              underscores allowed.
            </p>
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text">Description</span>
            </label>
            <textarea
              {...register("content")}
              className="textarea  w-full mb-2"
              rows={2}
            />
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text">Hashtags</span>
            </label>
            <HashtagList
              hashtags={hashtags}
              onHashtagsChange={setHashtags}
              placeholder="Add hashtag"
              className="mb-2"
            />
          </div>
        </>
      </Card>

      <Card>
        <>
          <fieldset className="fieldset">
            <legend className="fieldset-legend">
              Auto Add (Metadata Search)
            </legend>
            <input
              type="text"
              {...register("auto_add_search")}
              className="input w-full"
              placeholder="Search terms to automatically add this content"
            />
            <p className="label">
              If the photo metadata contains these terms, this description will
              be automatically added when posting.
            </p>
          </fieldset>

          <div className="flex gap-2">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Description Position</span>
              </label>
              <select
                {...register("position")}
                className="select select-bordered w-full mb-2"
              >
                <option value="USE_TEMPLATE">Use template</option>
                <option value="END">At the end of the post</option>
                <option value="START">At the beginning of the post</option>
                <option value="INLINE">Inline</option>
              </select>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Hashtag Position</span>
              </label>
              <select
                {...register("hashtag_position")}
                className="select select-bordered w-full mb-2"
              >
                <option value="START">At the start of hashtags</option>
                <option value="END">At the end of hashtags</option>
                <option value="INLINE">Custom position</option>
              </select>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Tag Position</span>
              </label>
              <select
                {...register("tag_position")}
                className="select select-bordered w-full mb-2"
              >
                <option value="START">At the start of tags</option>
                <option value="END">At the end of tags</option>
                <option value="INLINE">Custom position</option>
              </select>
            </div>
          </div>
        </>
      </Card>

      <div className="mt-4 mb-2">
        <div className="prose">
          <h3 className="font-medium">Variants</h3>
        </div>
      </div>

      <ul className="timeline timeline-vertical timeline-compact">
        {variantsList.map((v, i) => (
          <li
            key={v}
            style={{
              gridTemplateColumns: "0px 16px auto",
            }}
          >
            {i > 0 && <hr className="bg-neutral opacity-50" />}
            <div className="timeline-middle">
              <Disc size={16} />
            </div>
            <div className="timeline-end pl-4 pr-1 w-full">
              <Card>
                <div key={v} className="form-control">
                  <div className="flex items-center justify-between">
                    <label className="label">
                      <span className="label-text">Variant: {v}</span>
                    </label>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs"
                      onClick={() => removeVariant(v)}
                    >
                      <X />
                    </button>
                  </div>

                  <textarea
                    {...register(`variants.${v}`)}
                    className="textarea  w-full mb-2"
                    rows={2}
                  />

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Hashtags for {v}</span>
                    </label>
                    <HashtagList
                      hashtags={variantHashtags[v] || []}
                      onHashtagsChange={(newHashtags) =>
                        setVariantHashtags((prev) => ({
                          ...prev,
                          [v]: newHashtags,
                        }))
                      }
                      placeholder="Add hashtag for variant"
                      className="mb-2"
                    />
                  </div>
                </div>
              </Card>
            </div>

            <hr className="bg-neutral opacity-50" />
          </li>
        ))}

        <li
          style={{
            gridTemplateColumns: "0px 16px auto",
          }}
        >
          {variantsList.length > 0 && <hr className="bg-neutral opacity-50" />}
          <div className="timeline-middle">
            <Disc size={16} />
          </div>
          <div className="timeline-end pl-4 pr-1 w-full flex-1">
            {filteredFeeds && filteredFeeds.length > 0 && (
              <div className="form-control w-full">
                <div className="flex items-center gap-4 w-full">
                  <select className="select grow" ref={variantRef}>
                    {filteredFeeds.map((f) => (
                      <option value={f.slug!}>{f.name}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-outline"
                    type="button"
                    onClick={addVariant}
                  >
                    <Plus />
                  </button>
                </div>
              </div>
            )}
          </div>
        </li>
      </ul>

      <div className="flex justify-between mt-6 mb-8">
        <button type="button" className="btn btn-primary" onClick={onSubmit}>
          {!id ? "Add Saved Description" : "Update"}
        </button>
        {id && (
          <button type="button" className="btn btn-ghost" onClick={deleteDesc}>
            <Trash2 />
          </button>
        )}
      </div>
    </form>
  );
}

function SavedDescriptionDetails({
  description,
  variant = "main",
  isEditing,
}: {
  description: SavedContent;
  isEditing: boolean;
  variant?: string;
}) {
  const isMain = variant === "main";
  let hashtags: string[] = description.hashtags?.main || [];

  if (variant !== "main" && description.hashtags?.variants) {
    hashtags = description.hashtags.variants[variant] || [];
  }

  return (
    <>
      <Card className="card bg-base-100 shadow-sm mb-4">
        <>
          <div className="flex items-center gap-2 mb-1">
            <div className="prose">
              <h4>{isMain ? description.name : variant}</h4>
            </div>

            {isMain && description.auto_add_search && (
              <div
                className="badge badge-primary badge-sm tooltip tooltip-top"
                data-tip={`Auto-adds when photos contain: ${description.auto_add_search}`}
              >
                🔍 Auto: {description.auto_add_search}
              </div>
            )}
          </div>

          <p className="text-sm mb-2">{description.content}</p>

          {hashtags && hashtags.length > 0 && (
            <HashtagList
              hashtags={hashtags}
              onHashtagsChange={() => {}} // Read-only display
              className="pointer-events-none"
              displayOnly={!isEditing}
            />
          )}
        </>
      </Card>
      {isMain && (
        <Card>
          <>
            <div className="prose">
              <h4>Positions</h4>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {description.position && (
                <div>
                  <span className="font-bold">Description: </span>{" "}
                  {formatPosition(description.position)}
                </div>
              )}

              {description.hashtag_position && (
                <div>
                  <span className="font-bold">Hashtag: </span>
                  {formatPosition(description.hashtag_position)}
                </div>
              )}

              {description.tag_position && (
                <div>
                  <span className="font-bold">Tag: </span>
                  {formatPosition(description.tag_position)}
                </div>
              )}
            </div>

            <div className="prose mt-4">
              <h4>Use in Post Templates</h4>
            </div>
            <div className="">
              <div className="mockup-code bg-base-200 text-neutral w-full">
                <pre>
                  <code>
                    {"{{"}savedDescription {description.slug} {description.id}
                    {"}}"}
                  </code>
                </pre>
              </div>
            </div>
          </>
        </Card>
      )}
    </>
  );
}
